import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Loader2, MessageCircle, Copy, FileSpreadsheet } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { formatIDR, formatDateID } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { buildWhatsAppFollowupDue, buildWhatsAppFollowupOverdue } from '../lib/whatsapp';

export default function Receivables() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [bucket, setBucket] = useState('all');
  const [waOpen, setWaOpen] = useState(false);
  const [waReceivable, setWaReceivable] = useState(null);
  const [waTab, setWaTab] = useState('due');
  const [fupOpen, setFupOpen] = useState(false);
  const [fupData, setFupData] = useState({ channel: 'WhatsApp', follow_up_result: '', next_follow_up_date: '', receivable_id: null, invoice_id: null, client_id: null });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (status !== 'all') params.payment_status = status;
      if (bucket !== 'all') params.aging_bucket = bucket;
      const { data } = await api.get('/receivables', { params });
      setList(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, status, bucket]);

  const openWa = (r) => { setWaReceivable(r); setWaTab(r.payment_status === 'Overdue' ? 'overdue' : 'due'); setWaOpen(true); };
  const openFup = (r) => {
    setFupData({ channel: 'WhatsApp', follow_up_result: '', next_follow_up_date: '', receivable_id: r.id, invoice_id: r.invoice_id, client_id: r.client_id });
    setFupOpen(true);
  };

  const saveFup = async (e) => {
    e.preventDefault();
    try {
      await api.post('/followups', {
        ...fupData,
        follow_up_date: new Date().toISOString(),
        next_follow_up_date: fupData.next_follow_up_date ? new Date(fupData.next_follow_up_date + 'T00:00:00Z').toISOString() : null,
      });
      toast.success('Follow-up disimpan');
      setFupOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Tersalin'); } catch { toast.error('Gagal'); }
  };

  const exportCsv = () => {
    const rows = [['Tgl Event', 'Venue', 'Event', 'Client', 'No Invoice', 'Jatuh Tempo', 'Aging', 'Bucket', 'Status', 'Invoice', 'Terbayar', 'Outstanding']];
    list.forEach(r => rows.push([
      formatDateID(r.event_date), r.venue || '', r.event_description || '', r.client_name || '',
      r.invoice_number || '', formatDateID(r.payment_due_date), r.aging_days, r.aging_bucket,
      r.payment_status, r.invoice_amount, r.paid_amount, r.outstanding_amount,
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `piutang-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV diekspor');
  };

  const waMessageDue = waReceivable ? buildWhatsAppFollowupDue(waReceivable) : '';
  const waMessageOverdue = waReceivable ? buildWhatsAppFollowupOverdue(waReceivable) : '';

  return (
    <div className="space-y-4" data-testid="receivables-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="receivables-search-input" placeholder="Cari nomor, event, venue…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="Invoice Sent">Invoice Terkirim</SelectItem>
            <SelectItem value="DP Paid">DP Dibayar</SelectItem>
            <SelectItem value="Partial Paid">Sebagian</SelectItem>
            <SelectItem value="Paid">Lunas</SelectItem>
            <SelectItem value="Overdue">Overdue</SelectItem>
            <SelectItem value="Dispute">Dispute</SelectItem>
            <SelectItem value="Bad Debt">Bad Debt</SelectItem>
          </SelectContent>
        </Select>
        <Select value={bucket} onValueChange={setBucket}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Aging</SelectItem>
            <SelectItem value="Current">Current</SelectItem>
            <SelectItem value="Due Today">Due Today</SelectItem>
            <SelectItem value="1-7 Days">1-7 Hari</SelectItem>
            <SelectItem value="8-14 Days">8-14 Hari</SelectItem>
            <SelectItem value="15-30 Days">15-30 Hari</SelectItem>
            <SelectItem value="31-60 Days">31-60 Hari</SelectItem>
            <SelectItem value="60+ Days">60+ Hari</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="secondary" onClick={exportCsv}><FileSpreadsheet className="h-4 w-4 mr-2" />Ekspor CSV</Button>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="receivables-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Tgl Event</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>No Invoice</TableHead>
                  <TableHead>Jatuh Tempo</TableHead>
                  <TableHead className="text-right">Aging</TableHead>
                  <TableHead>Bucket</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Invoice</TableHead>
                  <TableHead className="text-right">Terbayar</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…</TableCell></TableRow>}
                {!loading && list.length === 0 && <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">Tidak ada piutang.</TableCell></TableRow>}
                {list.map(r => (
                  <TableRow key={r.id} className="hover:bg-white/4 border-border" data-testid={`receivables-row-${r.id}`}>
                    <TableCell className="text-muted-foreground">{formatDateID(r.event_date)}</TableCell>
                    <TableCell className="text-muted-foreground truncate max-w-[140px]">{r.venue}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{r.client_name}</span>
                        {r.include_ppn && (
                          <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-medium">PPN</span>
                        )}
                        {r.include_withholding_tax && (
                          <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
                            r.withholding_status === 'Received'
                              ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
                              : 'border-amber-400/30 bg-amber-400/10 text-amber-300'
                          }`} title={`Bukti Potong: ${r.withholding_status || 'Waiting'}`}>
                            PPh {r.withholding_status === 'Received' ? '✓' : '⏳'}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono-num text-xs cursor-pointer hover:underline" onClick={() => navigate(`/invoices/${r.invoice_id}`)}>{r.invoice_number}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDateID(r.payment_due_date)}</TableCell>
                    <TableCell className="text-right font-mono-num">{r.aging_days} hari</TableCell>
                    <TableCell><StatusBadge status={r.aging_bucket} /></TableCell>
                    <TableCell><StatusBadge status={r.payment_status} /></TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(r.invoice_amount)}</TableCell>
                    <TableCell className="text-right font-mono-num text-emerald-300">{formatIDR(r.paid_amount)}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(r.outstanding_amount)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openFup(r)} data-testid={`receivables-followup-${r.id}`}>Follow-up</Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openWa(r)} data-testid={`receivables-wa-${r.id}`}><MessageCircle className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp Composer */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-xl bg-card border-border">
          <DialogHeader>
            <DialogTitle>Template Pesan WhatsApp</DialogTitle>
            <DialogDescription>Pilih template, lalu salin pesan untuk dikirim via WhatsApp.</DialogDescription>
          </DialogHeader>
          <Tabs value={waTab} onValueChange={setWaTab}>
            <TabsList>
              <TabsTrigger value="due">Pengingat Jatuh Tempo</TabsTrigger>
              <TabsTrigger value="overdue">Follow-up Overdue</TabsTrigger>
            </TabsList>
            <TabsContent value="due">
              <Textarea rows={12} value={waMessageDue} readOnly className="font-mono-num text-xs" />
              <div className="flex justify-end mt-2"><Button onClick={() => copy(waMessageDue)} data-testid="wa-copy-due"><Copy className="h-4 w-4 mr-2" />Salin</Button></div>
            </TabsContent>
            <TabsContent value="overdue">
              <Textarea rows={12} value={waMessageOverdue} readOnly className="font-mono-num text-xs" />
              <div className="flex justify-end mt-2"><Button onClick={() => copy(waMessageOverdue)} data-testid="wa-copy-overdue"><Copy className="h-4 w-4 mr-2" />Salin</Button></div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Follow-up Dialog */}
      <Dialog open={fupOpen} onOpenChange={setFupOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader><DialogTitle>Tambah Follow-up</DialogTitle></DialogHeader>
          <form onSubmit={saveFup} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Channel</Label>
              <Select value={fupData.channel} onValueChange={(v) => setFupData({ ...fupData, channel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Call">Telepon</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="Meeting">Pertemuan</SelectItem>
                  <SelectItem value="Other">Lainnya</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Hasil Follow-up</Label>
              <Textarea rows={3} value={fupData.follow_up_result} onChange={(e) => setFupData({ ...fupData, follow_up_result: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Follow-up Berikutnya</Label>
              <Input type="date" value={fupData.next_follow_up_date} onChange={(e) => setFupData({ ...fupData, next_follow_up_date: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setFupOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="followup-save-button">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
